import { Injectable } from "@angular/core";
import dayjs from "dayjs";
import { assertUnreachable } from "../../../../../admin/shared/functions/assert-unreachable";
import { PublicationTemplateType } from "../../../../../brand/brand-campaign/publication-template/data/entities/publication-templates.entity";
import { SchedulePeriod } from "../../../../../brand/brand-campaign/publication-template/domain/models/publication-template";
import { PublicationAggregatedSummary } from "../../../domain/models/publication";
import { PublicationAggregatedCalendarViewItem } from "../../../domain/models/publication-calendar-view";
import { PublicationWithSummaryEventInput } from "../publication-with-summary-event-input";
import {
  FacebookCarouselWithSummaryCalendarEventFactory,
  FacebookPostWithSummaryCalendarEventFactory,
  FacebookStoryWithSummaryCalendarEventFactory,
} from "./facebook-publication-calendar-event.mappers";
import { GooglePerformanceMaxWithSummaryCalendarEventFactory } from "./google-publication-calendar-event.mappers";
import {
  InstagramSingleImageWithSummaryCalendarEventFactory,
  InstagramStoryWithSummaryCalendarEventFactory,
} from "./instagram-publication-calendar-event.mappers";

export const mapPublicationWithSummaryToCalendarEvents = (
  publication: PublicationAggregatedCalendarViewItem,
  period: SchedulePeriod,
  summary?: PublicationAggregatedSummary,
): PublicationWithSummaryEventInput => ({
  // todo: when the calendar allows editing => id: `${schedule.id}`,
  start: period.start.toISOString(),
  // fullCalendar does not display events on the end date, so we add one day
  end: dayjs(period.end?.toISOString())
    .add(1, "day")
    .toISOString(),
  allDay: true,
  durationEditable: false,
  eventResizableFromStart: false,
  eventDurationEditable: false,
  startEditable: false,
  extendedProps: {
    typeText: publication.publicationTemplateType,
    publicationTemplateId: publication.publicationTemplateId,
    platform: publication.platform,
    summary: summary,
    icon: "",
    title: "",
    image: "",
  },
});

export interface IPublicationWithSummaryCalendarEventFactory {
  create(
    publication: PublicationAggregatedCalendarViewItem,
  ): PublicationWithSummaryEventInput[];
}

@Injectable()
export class PublicationWithSummaryCalendarEventFactory
  implements IPublicationWithSummaryCalendarEventFactory
{
  public create(
    publication: PublicationAggregatedCalendarViewItem,
  ): PublicationWithSummaryEventInput[] {
    let factory: IPublicationWithSummaryCalendarEventFactory;

    switch (publication.publicationTemplateType) {
      case PublicationTemplateType.InstagramStory:
        factory = new InstagramStoryWithSummaryCalendarEventFactory();
        break;
      case PublicationTemplateType.InstagramSingleImage:
        factory = new InstagramSingleImageWithSummaryCalendarEventFactory();
        break;
      case PublicationTemplateType.FacebookPost:
        factory = new FacebookPostWithSummaryCalendarEventFactory();
        break;
      case PublicationTemplateType.FacebookCarousel:
        factory = new FacebookCarouselWithSummaryCalendarEventFactory();
        break;
      case PublicationTemplateType.FacebookStory:
        factory = new FacebookStoryWithSummaryCalendarEventFactory();
        break;
      case PublicationTemplateType.GooglePerformanceMax:
        factory = new GooglePerformanceMaxWithSummaryCalendarEventFactory();
        break;
      default:
        return assertUnreachable(publication as never);
    }

    return factory.create(publication);
  }
}
